import { useCallback, useEffect, useRef, useState } from "react";

/**
 * TRIAGEM DE CURRÍCULO — a página que o CANDIDATO abre.
 *
 * É o link enviado por WhatsApp: /triagem/<token>. Não tem login (o token é a
 * credencial) e só mostra o que o candidato precisa ver — vaga, setor e
 * perguntas. Nada do cadastro do RH aparece aqui.
 *
 * Reaproveita a casca da avaliação pública (classes `aval-*`): as duas páginas
 * são a mesma coisa do ponto de vista de quem recebe um link e responde.
 */

interface Pergunta {
  id: string;
  texto: string;
}

interface Vaga {
  nome: string;
  setor: string | null;
  cargo: string | null;
  descricao: string | null;
  perguntas: Pergunta[];
}

/** Casca comum (cabeçalho SERCAMP + cartão), igual à da avaliação. */
function Casca({ children }: { children: React.ReactNode }) {
  return (
    <div className="aval-publica">
      <div className="aval-header">
        <span className="aval-brand">SERCAMP</span>
        <span className="aval-brand-sub">Triagem de Currículo</span>
      </div>
      <div className="aval-card">{children}</div>
      <p className="aval-rodape">
        RH SERCAMP · não pedimos dados bancários nem pagamento em nenhuma etapa
      </p>
    </div>
  );
}

export default function TriagemPublica({ token }: { token: string }) {
  const [vaga, setVaga] = useState<Vaga | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  const [candidato, setCandidato] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [respostas, setRespostas] = useState<Record<string, string>>({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState<string | null>(null);

  /**
   * CURRÍCULO. O arquivo é escolhido antes de enviar, mas só sobe DEPOIS que as
   * respostas foram gravadas — é a resposta que dá o endereço da pasta do
   * candidato. Se o upload falhar, as respostas já estão salvas e a tela de
   * "recebido" oferece reenviar só o arquivo.
   */
  const [curriculo, setCurriculo] = useState<File | null>(null);
  const [respostaId, setRespostaId] = useState<number | null>(null);
  const [curriculoOk, setCurriculoOk] = useState(false);
  const [avisoCurriculo, setAvisoCurriculo] = useState<string | null>(null);
  const [subindo, setSubindo] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const MAX_MB = 10;

  /** Envia o arquivo para a pasta do candidato. Devolve true se subiu. */
  const enviarCurriculo = useCallback(
    async (id: number, arquivo: File): Promise<boolean> => {
      setSubindo(true);
      try {
        const fd = new FormData();
        fd.append("file", arquivo);
        const res = await fetch(
          `/api/triagem/formulario/${encodeURIComponent(token)}/curriculo/${id}`,
          { method: "POST", body: fd },
        );
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.error ?? "Não foi possível anexar o currículo.");
        setCurriculoOk(true);
        setAvisoCurriculo(null);
        return true;
      } catch (e) {
        setAvisoCurriculo(
          e instanceof Error ? e.message : "Não foi possível anexar o currículo.",
        );
        return false;
      } finally {
        setSubindo(false);
      }
    },
    [token],
  );

  function escolherCurriculo(files: FileList | null) {
    setAvisoCurriculo(null);
    const arquivo = files?.[0] ?? null;
    if (!arquivo) {
      setCurriculo(null);
      return;
    }
    // Checagem amiga no aparelho; o servidor valida de novo (é ele quem manda).
    const ext = arquivo.name.split(".").pop()?.toLowerCase() ?? "";
    if (!["pdf", "doc", "docx", "png", "jpg", "jpeg"].includes(ext)) {
      setAvisoCurriculo("Envie o currículo em PDF, Word ou foto (JPG/PNG).");
      setCurriculo(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (arquivo.size > MAX_MB * 1024 * 1024) {
      setAvisoCurriculo(`Arquivo muito grande (máximo ${MAX_MB} MB).`);
      setCurriculo(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setCurriculo(arquivo);
  }

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const res = await fetch(`/api/triagem/formulario/${encodeURIComponent(token)}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? "Link indisponível.");
      setVaga(body as Vaga);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível abrir o formulário.");
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!candidato.trim()) {
      setErro("Escreva o seu nome completo.");
      return;
    }
    setEnviando(true);
    try {
      const res = await fetch(`/api/triagem/formulario/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidato: candidato.trim(),
          email: email.trim(),
          telefone: telefone.trim(),
          respostas: (vaga?.perguntas ?? []).map((p) => ({
            id: p.id,
            resposta: respostas[p.id] ?? "",
          })),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? "Não foi possível enviar.");
      const id = Number(body?.id);
      if (Number.isInteger(id)) setRespostaId(id);
      // Respostas gravadas: agora sobe o currículo, se a pessoa anexou um.
      if (curriculo && Number.isInteger(id)) await enviarCurriculo(id, curriculo);
      setEnviado(body?.mensagem ?? "Resposta enviada. Obrigado!");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível enviar as respostas.");
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <Casca>
        <div className="empty">Abrindo o formulário…</div>
      </Casca>
    );
  }

  if (!vaga) {
    return (
      <Casca>
        <div className="aval-estado">
          <h2>Link indisponível</h2>
          <p>{erro ?? "Este link não está mais válido."}</p>
          <p>Peça um link novo ao RH da SERCAMP.</p>
        </div>
      </Casca>
    );
  }

  if (enviado) {
    return (
      <Casca>
        <div className="aval-estado">
          <div className="aval-ok-icon">✓</div>
          <h2>Recebido!</h2>
          <p>{enviado}</p>

          {curriculoOk && <p className="triagem-curriculo-ok">Currículo anexado com sucesso.</p>}

          {/* O currículo pode falhar depois das respostas já gravadas (rede do
              celular). Em vez de perder o arquivo, a pessoa reenvia só ele. */}
          {!curriculoOk && respostaId != null && (
            <div className="triagem-curriculo-publico">
              <p>
                {avisoCurriculo
                  ? `Suas respostas foram salvas, mas o currículo não subiu: ${avisoCurriculo}`
                  : "Quer anexar seu currículo? (opcional)"}
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                onChange={(e) => escolherCurriculo(e.target.files)}
              />
              <button
                type="button"
                className="btn-primary"
                disabled={!curriculo || subindo}
                onClick={() => curriculo && respostaId != null && enviarCurriculo(respostaId, curriculo)}
              >
                {subindo ? "Enviando…" : "Enviar currículo"}
              </button>
            </div>
          )}
        </div>
      </Casca>
    );
  }

  return (
    <Casca>
      <div className="aval-intro">
        <h2 style={{ margin: "0 0 4px" }}>{vaga.nome}</h2>
        <p className="aval-cargo">
          {[vaga.cargo, vaga.setor].filter(Boolean).join(" · ") || "Processo seletivo SERCAMP"}
        </p>
        {vaga.descricao && <p>{vaga.descricao}</p>}
        <p>Responda com suas palavras — leva poucos minutos.</p>
      </div>

      <form onSubmit={enviar} className="triagem-form">
        <label className="field">
          <span>
            Seu nome completo <span className="req">*</span>
          </span>
          <input
            value={candidato}
            onChange={(e) => setCandidato(e.target.value)}
            placeholder="Nome e sobrenome"
            autoComplete="name"
          />
        </label>

        <div className="triagem-contato">
          <label className="field">
            <span>WhatsApp</span>
            <input
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              placeholder="(19) 90000-0000"
              inputMode="tel"
              autoComplete="tel"
            />
          </label>
          <label className="field">
            <span>E-mail</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              inputMode="email"
              autoComplete="email"
            />
          </label>
        </div>

        {/* CURRÍCULO — o candidato anexa aqui mesmo, do celular. */}
        <label className="field triagem-curriculo-campo">
          <span>Seu currículo (opcional)</span>
          <p className="triagem-curriculo-dica">
            PDF, Word ou uma foto do currículo — até {MAX_MB} MB. Anexar ajuda o RH a avaliar você
            mais rápido.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            onChange={(e) => escolherCurriculo(e.target.files)}
          />
          {curriculo && (
            <small className="triagem-curriculo-nome">
              Selecionado: <strong>{curriculo.name}</strong> (
              {(curriculo.size / 1048576).toFixed(1)} MB)
            </small>
          )}
          {avisoCurriculo && <small className="form-error">{avisoCurriculo}</small>}
        </label>

        {vaga.perguntas.length === 0 ? (
          <p className="aval-cargo">
            Esta vaga ainda não tem perguntas. Envie seus dados que o RH entra em contato.
          </p>
        ) : (
          vaga.perguntas.map((p, i) => (
            <label key={p.id} className="field triagem-pergunta">
              <span>
                {i + 1}. {p.texto}
              </span>
              <textarea
                rows={3}
                value={respostas[p.id] ?? ""}
                onChange={(e) => setRespostas({ ...respostas, [p.id]: e.target.value })}
                placeholder="Responda com suas palavras"
              />
            </label>
          ))
        )}

        {erro && <div className="form-error">{erro}</div>}

        <div className="aval-foot">
          <span className="aval-contador">
            {vaga.perguntas.filter((p) => (respostas[p.id] ?? "").trim()).length} de{" "}
            {vaga.perguntas.length} respondida(s)
          </span>
          <button type="submit" className="btn-primary" disabled={enviando}>
            {enviando ? "Enviando…" : "Enviar respostas"}
          </button>
        </div>
      </form>
    </Casca>
  );
}
